import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useAuth } from "./AuthContext";

const LovedDealsContext = createContext(null);

export const useLovedDeals = () => {
  const value = useContext(LovedDealsContext);
  if (!value) throw new Error("useLovedDeals must be used inside LovedDealsProvider");
  return value;
};

export function LovedDealsProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [lovedIds, setLovedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setLovedIds(new Set());
      return [];
    }
    try {
      const { data } = await api.get("/loved-deals");
      const deals = Array.isArray(data) ? data : [];
      setLovedIds(new Set(deals.map((deal) => deal.id).filter(Boolean)));
      return deals;
    } catch {
      return [];
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!user) return;
    const pendingId = localStorage.getItem("pending_loved_deal");
    if (!pendingId) return;
    localStorage.removeItem("pending_loved_deal");
    api.post(`/loved-deals/${encodeURIComponent(pendingId)}`)
      .then(() => {
        setLovedIds((previous) => new Set(previous).add(pendingId));
        toast.success("Saved to your collections");
      })
      .catch(() => toast.error("We couldn't add that collection. Please try again."));
  }, [user]);

  const toggle = useCallback(async (deal) => {
    const id = typeof deal === "string" ? deal : deal?.id;
    if (!id || loading) return false;
    if (!user) {
      localStorage.setItem("pending_loved_deal", id);
      localStorage.setItem("pending_auth_return", location.pathname + location.search);
      toast.message("Sign in to love this deal");
      navigate("/auth");
      return false;
    }

    const wasLoved = lovedIds.has(id);
    setLoading(true);
    setLovedIds((previous) => {
      const next = new Set(previous);
      if (wasLoved) next.delete(id); else next.add(id);
      return next;
    });
    try {
      if (wasLoved) {
        await api.delete(`/loved-deals/${encodeURIComponent(id)}`);
        toast.success("Removed from your saved collections");
      } else {
        await api.post(`/loved-deals/${encodeURIComponent(id)}`);
        toast.success("Saved to your collections");
      }
      return true;
    } catch {
      setLovedIds((previous) => {
        const next = new Set(previous);
        if (wasLoved) next.add(id); else next.delete(id);
        return next;
      });
      toast.error("We couldn't update your saved collections. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [loading, location.pathname, location.search, lovedIds, navigate, user]);

  const value = useMemo(() => ({ lovedIds, loading, refresh, toggle }), [lovedIds, loading, refresh, toggle]);
  return <LovedDealsContext.Provider value={value}>{children}</LovedDealsContext.Provider>;
}
